import { posts as blogPosts } from "virtual:blog-index";

export type RouteSeo = {
  title: string;
  description: string;
  socialImage?: string;
  socialImageAlt?: string;
  indexable: boolean;
  sitemap: boolean;
  render: boolean;
  article?: {
    published: string;
    modified?: string;
    author: string;
    tags: string[];
  };
};

export const SITE_URL = "https://stylome.io";
export const SOCIAL_IMAGE = `${SITE_URL}/social-preview.png?v=20260915-reskin`;

export const SEO_ROUTES: Record<string, RouteSeo> = {
  "/": {
    title: "AI Text Detector for Essays, Posts and Emails | Stylome",
    description:
      "Paste text or import a DOCX or EPUB file locally, then scan a reviewable passage with Stylome. See published detection and false-positive results.",
    indexable: true,
    sitemap: true,
    render: true,
  },
  "/transparency": {
    title: "AI Text Detection Accuracy and Public Benchmark Results | Stylome",
    description:
      "Read dated public benchmark reports for each Stylome model, with sample counts, false-positive rates, training-overlap checks, and measurement limits.",
    indexable: true,
    sitemap: true,
    render: true,
  },
  "/methodology": {
    title: "How Stylome Tests AI-Text Detection | Stylome",
    description:
      "See how Stylome audits overlap, freezes holdouts, compares detectors at matched false-positive rates, and evaluates results by writing source and register.",
    indexable: true,
    sitemap: true,
    render: true,
  },
  "/developers": {
    title: "AI Text Detection API Documentation | Stylome",
    description:
      "Use the early-alpha Stylome API with prepaid credit and character-based pricing. API access is billed separately from the personal extension plan.",
    indexable: true,
    sitemap: true,
    render: true,
  },
  "/pricing": {
    title: "Stylome Pricing: Free Scanner, Premium and API | Stylome",
    description:
      "Scan text free, choose Premium from $19 monthly, or use Stylome 1.0 Large and 1.2 Large APIs for $5 per million normalized input characters.",
    indexable: true,
    sitemap: true,
    render: true,
  },
  "/extension": {
    title: "Stylome for Chrome: See AI-Written Posts as You Browse | Stylome",
    description:
      "The Stylome Chrome extension marks AI-written posts, comments, and articles on Reddit and 400+ other sites. 14-day trial, no card required.",
    indexable: true,
    sitemap: true,
    render: true,
  },
  "/welcome": {
    title: "Welcome to Stylome | Stylome",
    description: "Finish setting up the Stylome browser extension.",
    indexable: false,
    sitemap: false,
    render: true,
  },
  "/supported-sites": {
    title: "Browser Extension Supported Sites | Stylome",
    description:
      "Search the current Stylome browser-extension catalog by site name, domain, or URL and check whether a dedicated integration is available.",
    socialImage: `${SITE_URL}/social-preview-supported-sites.png?v=20260915-reskin`,
    socialImageAlt:
      "Stylome browser extension: 405 dedicated site integrations; catalog checked 15 September 2026",
    indexable: true,
    sitemap: true,
    render: true,
  },
  "/bounty": {
    title: "Stylome Model Improvement Program | Stylome",
    description:
      "Submit verified pre-2022 human writing that earns an AI-generated result and help strengthen future Stylome models.",
    indexable: true,
    sitemap: true,
    render: true,
  },
  "/privacy": {
    title: "Privacy | Stylome",
    description:
      "Read how Stylome handles local DOCX and EPUB imports, submitted text, account identity, browser-extension scans, public receipts, retention, and deletion requests.",
    indexable: true,
    sitemap: false,
    render: true,
  },
  "/terms": {
    title: "Terms | Stylome",
    description:
      "Read the Stylome terms for personal extension access, trials, billing, refunds, submitted text, acceptable use, and classifier results.",
    indexable: true,
    sitemap: false,
    render: true,
  },
  "/account": {
    title: "Account | Stylome",
    description: "Manage your Stylome account and API keys.",
    indexable: false,
    sitemap: false,
    render: false,
  },
  "/login": {
    title: "Sign in | Stylome",
    description: "Sign in to your Stylome account.",
    indexable: false,
    sitemap: false,
    render: false,
  },
  "/register": {
    title: "Create account | Stylome",
    description: "Create a Stylome account.",
    indexable: false,
    sitemap: false,
    render: false,
  },
  "/verify-email": {
    title: "Verify email | Stylome",
    description: "Verify your Stylome account email.",
    indexable: false,
    sitemap: false,
    render: false,
  },
  "/forgot-password": {
    title: "Forgot password | Stylome",
    description: "Request a Stylome password reset.",
    indexable: false,
    sitemap: false,
    render: false,
  },
  "/reset-password": {
    title: "Reset password | Stylome",
    description: "Reset your Stylome account password.",
    indexable: false,
    sitemap: false,
    render: false,
  },
  "/500": {
    title: "Service error | Stylome",
    description: "Stylome could not render the requested page.",
    indexable: false,
    sitemap: false,
    render: true,
  },
  "/maintenance": {
    title: "Scheduled maintenance | Stylome",
    description:
      "Stylome is temporarily read-only during scheduled maintenance.",
    indexable: false,
    sitemap: false,
    render: true,
  },
};

// An empty index is thin content, so keep it out of search until a post exists.
const hasPublishedPost = blogPosts.some((post) => !post.draft);
SEO_ROUTES["/blog"] = {
  title: "Blog: Measuring AI-Written Text in the Wild | Stylome",
  description:
    "Dated, reproducible measurements of AI-written text in court opinions, darknet markets, forums, and other public writing, with methods and data.",
  indexable: hasPublishedPost,
  sitemap: hasPublishedPost,
  render: true,
};
for (const post of blogPosts) {
  SEO_ROUTES[`/blog/${post.slug}`] = {
    title: `${post.title} | Stylome`,
    description: post.description,
    socialImage: post.image ? new URL(post.image, SITE_URL).href : undefined,
    socialImageAlt: post.image ? post.imageAlt : undefined,
    indexable: !post.draft,
    sitemap: !post.draft,
    render: true,
    article: {
      published: post.date,
      modified: post.updated,
      author: post.author,
      tags: post.tags,
    },
  };
}

export const NOT_FOUND_SEO: RouteSeo = {
  title: "Page not found | Stylome",
  description: "The requested Stylome page does not exist.",
  indexable: false,
  sitemap: false,
  render: true,
};

export function normalizedRoute(route: string) {
  if (route === "/") return route;
  return route.replace(/\/$/, "");
}

export function seoForRoute(route: string): RouteSeo {
  const normalized = normalizedRoute(route);
  if (normalized.startsWith("/r/")) {
    return {
      title: "Classifier receipt | Stylome",
      description: "A shared Stylome classifier receipt.",
      indexable: true,
      sitemap: false,
      render: false,
    };
  }
  return SEO_ROUTES[normalized] || NOT_FOUND_SEO;
}

export function canonicalForRoute(route: string) {
  return `${SITE_URL}${normalizedRoute(route)}`;
}
